import type { Database } from '@areapif/areapif-shared'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { FC, useCallback, useEffect, useRef, useState } from 'react'

import { useEvidenceUpload } from '../../hooks'
import { getTranslate, type Locale } from '../../i18n'

type SealCategory = Database['public']['Enums']['seal_category']

interface AddSealsDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  inspectionId: string
  locale: Locale
  availableCategories: readonly SealCategory[]
}

export const AddSealsDialog: FC<AddSealsDialogProps> = ({ open, onClose, onSuccess, inspectionId, locale, availableCategories }) => {
  const t = getTranslate(locale)
  const { uploadSeals, isUploading, error } = useEvidenceUpload()

  const [files, setFiles] = useState<File[]>([])
  const [category, setCategory] = useState<SealCategory | ''>('')
  const [serialId, setSerialId] = useState('')

  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetState = useCallback(() => {
    setFiles([])
    setCategory('')
    setSerialId('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  useEffect(() => {
    if (open) {
      resetState()
    }
  }, [open, resetState])

  const handleFilesChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files
    if (selected) {
      setFiles(Array.from(selected))
    }
  }, [])

  const handleUpload = useCallback(async () => {
    if (files.length === 0 || category === '') {
      return
    }

    try {
      await uploadSeals(inspectionId, files, category, {
        serial_id: serialId.trim(),
        owner: '',
      })
      onSuccess()
      resetState()
      onClose()
    } catch {
      // error is surfaced via the `error` state from useEvidenceUpload
    }
  }, [files, category, serialId, uploadSeals, inspectionId, onSuccess, resetState, onClose])

  const handleClose = useCallback(() => {
    if (!isUploading) {
      resetState()
      onClose()
    }
  }, [isUploading, resetState, onClose])

  const isUploadDisabled = files.length === 0 || category === '' || isUploading

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('pages.historical_inspections.add_evidence.dialog_title_seals')}</DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error !== null && <Alert severity="error">{t('pages.historical_inspections.add_evidence.error')}</Alert>}

          <Stack spacing={1}>
            <Button variant="outlined" component="label" disabled={isUploading}>
              {t('pages.historical_inspections.add_evidence.select_file')}
              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFilesChange} />
            </Button>

            {files.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {files.map(f => f.name).join(', ')}
              </Typography>
            )}
          </Stack>

          <TextField
            select
            label={t('pages.historical_inspections.add_evidence.category')}
            value={category}
            onChange={e => {
              setCategory(e.target.value as SealCategory)
            }}
            disabled={isUploading}
            fullWidth
          >
            {availableCategories.map(value => (
              <MenuItem key={value} value={value}>
                {t(`resources.historical_inspections.enums.seal_category.${value}`)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={t('pages.historical_inspections.add_evidence.serial_id')}
            value={serialId}
            onChange={e => {
              setSerialId(e.target.value)
            }}
            disabled={isUploading}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading}>
          {t('pages.historical_inspections.add_evidence.cancel')}
        </Button>
        <Button
          onClick={() => {
            void handleUpload()
          }}
          variant="contained"
          disabled={isUploadDisabled}
          startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isUploading ? t('pages.historical_inspections.add_evidence.uploading') : t('pages.historical_inspections.add_evidence.upload')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
